import { useQuery } from '@tanstack/react-query'
import { getBillingStatus, createCheckout, type BillingStatus } from '@/lib/api/billing'

const BILLING_QUERY_KEY = (orgId: string) => ['billing', orgId]

export function useBilling(orgId: string) {
  const query = useQuery({
    queryKey: BILLING_QUERY_KEY(orgId),
    queryFn: () => getBillingStatus(orgId),
    enabled: !!orgId,
    // Poll every 5 seconds after checkout until ACTIVE (catches webhook delivery)
    refetchInterval: (query) => {
      const data = query.state.data
      // If data hasn't loaded yet, don't poll
      if (!data) return false
      const state = data.data.billingState
      // Stop polling once we've reached a known stable paid state
      if (state === 'ACTIVE') return false
      // Poll while transitioning (e.g. TRIALING after checkout, PAST_DUE, etc.)
      return 5_000
    },
  })

  const status: BillingStatus | undefined = query.data?.data

  return {
    billingState: status?.billingState ?? 'TRIALING',
    trialEndsAt: status?.trialEndsAt ?? null,
    daysRemaining: status?.daysRemaining ?? null,
    currentPeriodEndsAt: status?.currentPeriodEndsAt ?? null,
    entitlements: status?.entitlements ?? null,
    canExport: status?.entitlements.CAN_EXPORT ?? true,
    canUseCustomSubreqs: status?.entitlements.CAN_USE_CUSTOM_SUBREQS ?? true,
    isTrialing: status?.billingState === 'TRIALING',
    isActive: status?.billingState === 'ACTIVE' || status?.billingState === 'PAST_DUE',
    isCancelling: status?.billingState === 'CANCELLING',
    isExpired:
      status?.billingState === 'EXPIRED' || status?.billingState === 'CANCELLED',
    isLoading: query.isLoading,
  }
}

/**
 * Opens the Paddle checkout overlay for the given org.
 * Requires Paddle.js to be initialized in main.tsx before calling.
 */
export async function openCheckout(orgId: string, orgSlug: string): Promise<void> {
  const { data } = await createCheckout(orgId)

  // @ts-ignore — Paddle is loaded via CDN script tag
  const Paddle = (window as any).Paddle
  if (!Paddle) {
    console.error('Paddle.js not loaded')
    return
  }

  Paddle.Checkout.open({
    items: [{ priceId: data.priceId, quantity: 1 }],
    customer: { email: data.email },
    customData: { orgId: data.orgId },
    successUrl: `${window.location.origin}/orgs/${orgSlug}/billing?upgraded=true`,
  })
}

export { BILLING_QUERY_KEY }
